/**
 * Notification preferences — shape, defaults, labels, and legacy migration.
 *
 * Source of truth for both the Account settings UI and (in Step 5) the email
 * Cloud Functions. Mirrors the pattern from Radio Vault's notificationPreferences.ts.
 *
 * Storage: `organizations/{orgId}/members/{userId}.notifications`
 *
 * Legacy compatibility: prior to Step 3 the field was `{ licenseExpiry: boolean }`,
 * which gated the ExclusiveLicenseNotificationModal. `mergeWithDefaults` migrates
 * that field forward into `inApp.exclusiveLicenseEndings`.
 */

export type NotificationType =
  | 'pendingLicenses'
  | 'licenseRequests'
  | 'rejectedLicenseRequests'
  | 'licenseClaimed'
  | 'expiringSoonLicenses'
  | 'expiredLicenses'
  | 'exclusiveLicenseEndings';

export const NOTIFICATION_TYPES_ORDER: NotificationType[] = [
  'pendingLicenses',
  'licenseRequests',
  'rejectedLicenseRequests',
  'licenseClaimed',
  'expiringSoonLicenses',
  'expiredLicenses',
  'exclusiveLicenseEndings',
];

/** Friendly label shown in the settings table for each type. */
export const NOTIFICATION_LABELS: Record<NotificationType, string> = {
  pendingLicenses:         'A license was shared with me to claim',
  licenseRequests:         'Someone requested a license for my track',
  rejectedLicenseRequests: 'My license request was rejected',
  licenseClaimed:          'A license I issued was claimed',
  expiringSoonLicenses:    'A license I hold or issued is about to expire',
  expiredLicenses:         'A license I hold or issued expired or was revoked',
  exclusiveLicenseEndings: 'An exclusive license on my track ended',
};

/** Secondary description shown beneath each row in the settings UI. */
export const NOTIFICATION_DESCRIPTIONS: Record<NotificationType, string> = {
  pendingLicenses:         'Someone shared a license with your organization and is waiting for you to claim it.',
  licenseRequests:         'Another organization has requested a license for one of your tracks — approve or reject.',
  rejectedLicenseRequests: 'A license request you submitted was declined by the artist.',
  licenseClaimed:          'A pending license you issued (direct share or share link) has been claimed and is now active.',
  expiringSoonLicenses:    'A heads-up that a license you hold or issued is approaching its expiry date.',
  expiredLicenses:         'A license you hold or issued reached its expiry date or was revoked.',
  exclusiveLicenseEndings: 'An exclusive license on one of your tracks has ended — you can make the track public again.',
};

export type ChannelPrefs = Record<NotificationType, boolean>;

export interface NotificationPreferences {
  inApp: ChannelPrefs;
  email: ChannelPrefs;
}

/**
 * Default lead time (in days) before a license's `dateExpires` at which the
 * pre-expiry notification fires. Org-level setting stored on the organization
 * document as `licenseExpiryLeadTimeDays`. Admins can override per-org.
 */
export const DEFAULT_EXPIRY_LEAD_TIME_DAYS = 30;

/** Bounds for the lead-time setting in the admin UI. */
export const MIN_EXPIRY_LEAD_TIME_DAYS = 1;
export const MAX_EXPIRY_LEAD_TIME_DAYS = 365;

/** Default prefs applied when a user has none stored. In-app on, email off. */
export function defaultPreferences(): NotificationPreferences {
  return {
    inApp: {
      pendingLicenses:         true,
      licenseRequests:         true,
      rejectedLicenseRequests: true,
      licenseClaimed:          true,
      expiringSoonLicenses:    true,
      expiredLicenses:         true,
      exclusiveLicenseEndings: true,
    },
    email: {
      pendingLicenses:         false,
      licenseRequests:         false,
      rejectedLicenseRequests: false,
      licenseClaimed:          false,
      expiringSoonLicenses:    false,
      expiredLicenses:         false,
      exclusiveLicenseEndings: false,
    },
  };
}

/**
 * Merge stored prefs with defaults. Defensive against partial / missing
 * preference docs. Always returns a fully-populated NotificationPreferences.
 *
 * Also migrates the legacy `{ licenseExpiry: boolean }` field into the new
 * shape so existing users keep their setting for the Exclusive License
 * Endings modal (the only modal that previously consulted preferences).
 */
export function mergeWithDefaults(stored: any): NotificationPreferences {
  const def = defaultPreferences();
  if (!stored || typeof stored !== 'object') return def;

  const hasNewShape = stored.inApp || stored.email;
  const hasLegacyLicenseExpiry = typeof stored.licenseExpiry === 'boolean';

  const merged: NotificationPreferences = {
    inApp: { ...def.inApp },
    email: { ...def.email },
  };

  if (hasNewShape) {
    for (const channel of ['inApp', 'email'] as const) {
      const channelStored =
        stored[channel] && typeof stored[channel] === 'object' ? stored[channel] : {};
      for (const type of NOTIFICATION_TYPES_ORDER) {
        if (typeof channelStored[type] === 'boolean') {
          merged[channel][type] = channelStored[type];
        }
      }
    }
  }

  // Legacy field only gated the Exclusive License Endings modal.
  if (hasLegacyLicenseExpiry) {
    merged.inApp.exclusiveLicenseEndings = stored.licenseExpiry as boolean;
  }

  return merged;
}
